import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import type { TreatmentFormData } from "../treatmenttable/components/treatmentform/treatmentFormUtils";
import type { PrescriptionFormData } from "../prescription/prescriptionFormUtils";
import { addPrescription } from "../../../redux/diagnosestate/diagnoseAction";
import {
  countDrafts,
  createDraftId,
  draftStorageKey,
  dropSavedRows,
  emptyDrafts,
  emptyPlanForms,
  hasAnyDraft,
  loadDrafts,
  persistDrafts,
  type DraftCounts,
  type PlanCategory,
  type PlanFormState,
  type VisitDrafts,
} from "./treatmentDraftStore";
import {
  savePlanForms,
  savePrescriptionDraftRows,
  saveTreatmentDraftRows,
} from "./saveVisitDrafts";

export type DraftKind = "treatments" | "prescriptions" | "plan";

export interface VisitDraftsApi {
  drafts: VisitDrafts;
  counts: DraftCounts;
  hasDrafts: boolean;
  isSaving: boolean;
  /** Bumped after any save from the shell, so mounted tabs refetch. */
  savedAt: number;

  addTreatmentDraft: (data?: TreatmentFormData) => string;
  updateTreatmentDraft: (id: string, data: TreatmentFormData) => void;
  removeTreatmentDraft: (id: string) => void;

  addPrescriptionDraft: (data?: PrescriptionFormData) => string;
  updatePrescriptionDraft: (id: string, data: PrescriptionFormData) => void;
  removePrescriptionDraft: (id: string) => void;
  clearPrescriptionDrafts: () => void;

  setPlanForm: (
    category: PlanCategory,
    update: PlanFormState | ((prev: PlanFormState) => PlanFormState)
  ) => void;

  /** Save one kind. Resolves true when every row of that kind saved. */
  saveKind: (kind: DraftKind) => Promise<boolean>;
  /** Save treatments, then drugs, then plan rows. */
  saveAll: () => Promise<boolean>;
  discardAll: () => void;
}

const kindLabel: Record<DraftKind, [string, string]> = {
  treatments: ["treatment", "treatments"],
  prescriptions: ["prescription", "prescriptions"],
  plan: ["plan item", "plan items"],
};

const plural = (kind: DraftKind, n: number) =>
  `${n} ${n === 1 ? kindLabel[kind][0] : kindLabel[kind][1]}`;

/**
 * Owns every unsaved row of one patient visit. Lives in the treatment page
 * shell so tabs can mount and unmount freely.
 */
export function useVisitDrafts(
  treatmentId: string | undefined,
  appointmentId: string
): VisitDraftsApi {
  const dispatch = useDispatch();
  const key = draftStorageKey(treatmentId ?? "", appointmentId);
  // The drafts carry the key they were loaded for, so a visit swap never
  // persists the old visit's rows under the new visit's key.
  const [state, setState] = useState<{ key: string; drafts: VisitDrafts }>(
    () => ({ key, drafts: treatmentId ? loadDrafts(key) : emptyDrafts() })
  );
  const drafts = state.drafts;
  const setDrafts = useCallback(
    (update: VisitDrafts | ((prev: VisitDrafts) => VisitDrafts)) => {
      setState((prev) => {
        const next =
          typeof update === "function" ? update(prev.drafts) : update;
        return next === prev.drafts ? prev : { ...prev, drafts: next };
      });
    },
    []
  );
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  // Another visit opened in the same shell: swap to its drafts.
  useEffect(() => {
    setState((prev) =>
      prev.key === key
        ? prev
        : { key, drafts: treatmentId ? loadDrafts(key) : emptyDrafts() }
    );
  }, [key, treatmentId]);

  useEffect(() => {
    if (!treatmentId || state.key !== key) return;
    persistDrafts(key, state.drafts);
  }, [key, state, treatmentId]);

  // The browser's own leave-page confirm while rows are unsaved.
  const hasDrafts = hasAnyDraft(drafts);
  useEffect(() => {
    if (!hasDrafts) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasDrafts]);

  const addTreatmentDraft = useCallback((data?: TreatmentFormData) => {
    const id = createDraftId();
    setDrafts((prev) => ({
      ...prev,
      treatments: [
        ...prev.treatments,
        { id, data: data ?? ({} as TreatmentFormData) },
      ],
    }));
    return id;
  }, [setDrafts]);

  const updateTreatmentDraft = useCallback(
    (id: string, data: TreatmentFormData) => {
      setDrafts((prev) => {
        const row = prev.treatments.find((r) => r.id === id);
        if (!row || (row.data === data && !row.error)) return prev;
        return {
          ...prev,
          treatments: prev.treatments.map((r) =>
            r.id === id ? { id, data } : r
          ),
        };
      });
    },
    [setDrafts]
  );

  const removeTreatmentDraft = useCallback((id: string) => {
    setDrafts((prev) => ({
      ...prev,
      treatments: prev.treatments.filter((r) => r.id !== id),
    }));
  }, [setDrafts]);

  const addPrescriptionDraft = useCallback((data?: PrescriptionFormData) => {
    const id = createDraftId();
    setDrafts((prev) => ({
      ...prev,
      prescriptions: [
        ...prev.prescriptions,
        { id, data: data ?? ({} as PrescriptionFormData) },
      ],
    }));
    return id;
  }, [setDrafts]);

  const updatePrescriptionDraft = useCallback(
    (id: string, data: PrescriptionFormData) => {
      setDrafts((prev) => {
        const row = prev.prescriptions.find((r) => r.id === id);
        if (!row || (row.data === data && !row.error)) return prev;
        return {
          ...prev,
          prescriptions: prev.prescriptions.map((r) =>
            r.id === id ? { id, data } : r
          ),
        };
      });
    },
    [setDrafts]
  );

  const removePrescriptionDraft = useCallback((id: string) => {
    setDrafts((prev) => ({
      ...prev,
      prescriptions: prev.prescriptions.filter((r) => r.id !== id),
    }));
  }, [setDrafts]);

  const clearPrescriptionDrafts = useCallback(() => {
    setDrafts((prev) =>
      prev.prescriptions.length ? { ...prev, prescriptions: [] } : prev
    );
  }, [setDrafts]);

  const setPlanForm = useCallback(
    (
      category: PlanCategory,
      update: PlanFormState | ((prev: PlanFormState) => PlanFormState)
    ) => {
      setDrafts((prev) => {
        const current = prev.plan[category];
        const next = typeof update === "function" ? update(current) : update;
        if (next.option === current.option && next.note === current.note) {
          return prev;
        }
        return { ...prev, plan: { ...prev.plan, [category]: next } };
      });
    },
    [setDrafts]
  );

  const runSave = useCallback(
    async (kind: DraftKind): Promise<boolean> => {
      if (!treatmentId) return false;
      const current = draftsRef.current;

      if (kind === "treatments") {
        if (current.treatments.length === 0) return true;
        const result = await saveTreatmentDraftRows(
          treatmentId,
          appointmentId,
          current.treatments
        );
        setDrafts((prev) => ({
          ...prev,
          treatments: dropSavedRows(prev.treatments, result.errors),
        }));
        if (result.savedIds.length) {
          toast.success(`${plural(kind, result.savedIds.length)} saved`);
        }
        Object.values(result.errors).forEach((m) => toast.error(m));
        return Object.keys(result.errors).length === 0;
      }

      if (kind === "prescriptions") {
        if (current.prescriptions.length === 0) return true;
        const result = await savePrescriptionDraftRows(
          treatmentId,
          current.prescriptions
        );
        result.saved.forEach(({ item }) => dispatch(addPrescription(item)));
        setDrafts((prev) => ({
          ...prev,
          prescriptions: dropSavedRows(prev.prescriptions, result.errors),
        }));
        if (result.savedIds.length) {
          toast.success(`${plural(kind, result.savedIds.length)} saved`);
        }
        Object.values(result.errors).forEach((m) => toast.error(m));
        return Object.keys(result.errors).length === 0;
      }

      const result = await savePlanForms(treatmentId, current.plan);
      if (result.savedCategories.length) {
        setDrafts((prev) => {
          const plan = { ...prev.plan };
          result.savedCategories.forEach((c) => {
            plan[c] = emptyPlanForms()[c];
          });
          return { ...prev, plan };
        });
        toast.success(`${plural(kind, result.savedCategories.length)} saved`);
      }
      Object.entries(result.errors).forEach(([c, m]) =>
        toast.error(`${c}: ${m}`)
      );
      return Object.keys(result.errors).length === 0;
    },
    [treatmentId, appointmentId, dispatch, setDrafts]
  );

  const saveKind = useCallback(
    async (kind: DraftKind) => {
      setIsSaving(true);
      try {
        const ok = await runSave(kind);
        setSavedAt(Date.now());
        return ok;
      } finally {
        setIsSaving(false);
      }
    },
    [runSave]
  );

  const saveAll = useCallback(async () => {
    setIsSaving(true);
    try {
      const treatmentsOk = await runSave("treatments");
      const prescriptionsOk = await runSave("prescriptions");
      const planOk = await runSave("plan");
      setSavedAt(Date.now());
      return treatmentsOk && prescriptionsOk && planOk;
    } finally {
      setIsSaving(false);
    }
  }, [runSave]);

  const discardAll = useCallback(() => {
    setDrafts(emptyDrafts());
  }, [setDrafts]);

  const counts = useMemo(() => countDrafts(drafts), [drafts]);

  return useMemo(
    () => ({
      drafts,
      counts,
      hasDrafts,
      isSaving,
      savedAt,
      addTreatmentDraft,
      updateTreatmentDraft,
      removeTreatmentDraft,
      addPrescriptionDraft,
      updatePrescriptionDraft,
      removePrescriptionDraft,
      clearPrescriptionDrafts,
      setPlanForm,
      saveKind,
      saveAll,
      discardAll,
    }),
    [
      drafts,
      counts,
      hasDrafts,
      isSaving,
      savedAt,
      addTreatmentDraft,
      updateTreatmentDraft,
      removeTreatmentDraft,
      addPrescriptionDraft,
      updatePrescriptionDraft,
      removePrescriptionDraft,
      clearPrescriptionDrafts,
      setPlanForm,
      saveKind,
      saveAll,
      discardAll,
    ]
  );
}
